import type { ReactNode } from 'react';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';

interface StepUpCodeFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** From `useStepUpWindow()`: while true there is no field, only a quiet note. */
  active: boolean;
  minutesLeft: number;
  /** Replaces the default label "Code from the authenticator app". */
  label?: string;
  /** Helper text under the field (or under the note while the window is open). */
  hint?: ReactNode;
  className?: string;
  /** Extra classes for the input, e.g. a max width. */
  inputClassName?: string;
  disabled?: boolean;
}

/**
 * The 2FA code field of a step-up form. Inside the 30-minute window it is replaced by a
 * one-line note (the form then sends no code).
 */
export function StepUpCodeField({
  id,
  value,
  onChange,
  active,
  minutesLeft,
  label = 'Code from the authenticator app',
  hint,
  className = 'flex flex-col gap-1.5',
  inputClassName,
  disabled,
}: StepUpCodeFieldProps) {
  const hintId = `${id}-hint`;
  if (active) {
    return (
      <div className={className}>
        <p className="m-0 text-xs text-muted" role="status">
          2FA confirmed. No code needed for the next {minutesLeft} min.
        </p>
        {hint ? <div className="text-xs text-muted">{hint}</div> : null}
      </div>
    );
  }
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123 456"
        className={cn('font-mono text-base tracking-widest', inputClassName)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hint ? hintId : undefined}
        disabled={disabled}
        required
      />
      {hint ? (
        <div id={hintId} className="text-xs text-muted">
          {hint}
        </div>
      ) : null}
    </div>
  );
}
